/**
 * LMS Connect Panel (Phase 4 — Steps 4.6, 4.7).
 *
 * UI for connecting to LMS and pulling/pushing attendance data.
 */

import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { CollapsiblePanel, Button } from '../shared';
import {
  type LMSProvider,
  type LMSConfig,
  DEFAULT_LMS_CONFIG,
  getLMSAuthUrl,
} from '@/integration/lmsConnector';

export function LmsConnectPanel(): React.ReactElement {
  const [config, setConfig] = useState<LMSConfig>(DEFAULT_LMS_CONFIG);
  const [status, setStatus] = useState<string>('');

  const handleConnect = (): void => {
    if (!config.baseUrl) {
      setStatus('Please enter your institution\'s LMS URL.');
      return;
    }
    const url = getLMSAuthUrl(config.provider);
    setStatus(`Redirecting to ${config.provider} for authorization... (stub: ${url.slice(0, 60)}...)`);
  };

  const handlePull = (): void => {
    setStatus('Pulling attendance data... (stub — not yet available)');
  };

  const handlePush = (): void => {
    setStatus('Pushing attendance grade... (stub — not yet available)');
  };

  return (
    <CollapsiblePanel title="LMS Integration" defaultOpen={false}>
      <div className="lms-connect-panel">
        <div className="lms-connect-panel__provider">
          <label>
            LMS Platform
            <select
              value={config.provider}
              onChange={(e) => setConfig({ ...config, provider: e.target.value as LMSProvider })}
            >
              <option value="canvas">Canvas</option>
              <option value="blackboard">Blackboard</option>
              <option value="moodle">Moodle</option>
            </select>
          </label>
        </div>

        <div className="lms-connect-panel__url">
          <label>
            Institution LMS URL
            <input
              type="url"
              placeholder="e.g., https://university.instructure.com"
              value={config.baseUrl ?? ''}
              onChange={(e) => setConfig({ ...config, baseUrl: e.target.value || null })}
            />
          </label>
        </div>

        <div className="lms-connect-panel__auth">
          {config.accessToken ? (
            <p className="lms-connect-panel__connected"><Check aria-hidden="true" /> Connected</p>
          ) : (
            <Button variant="primary" size="sm" onClick={handleConnect}>
              Connect to {config.provider.charAt(0).toUpperCase() + config.provider.slice(1)}
            </Button>
          )}
        </div>

        {config.accessToken && (
          <div className="lms-connect-panel__actions">
            <Button variant="secondary" size="sm" onClick={handlePull}>
              Pull Attendance Data
            </Button>
            <Button variant="secondary" size="sm" onClick={handlePush}>
              Push Attendance Grade
            </Button>
          </div>
        )}

        {status && <p className="lms-connect-panel__status">{status}</p>}
      </div>
    </CollapsiblePanel>
  );
}
